import React, { useMemo } from 'react';
import {
    BodyMediumRegular,
    DownloadIcon,
    Icon,
    type IconType,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    type BankAccountDisplayDetail,
    type IncomeSplitConfig,
    type IncomeSplitConfigLegacy,
} from '../types/IncomeSplit';

export const IncomeSplitDetails = ({
    incomeSplit,
    totalEligibleAccounts,
    icon,
    statusBadge,
    onIconPress,
    verifyNumberOfAccountsWatched = true,
}: {
    incomeSplit: IncomeSplitConfigLegacy | IncomeSplitConfig;
    totalEligibleAccounts: BankAccountDisplayDetail[];
    icon?: IconType;
    statusBadge?: React.ReactNode;
    onIconPress?: () => void;
    verifyNumberOfAccountsWatched?: boolean;
}) => {
    const allEligibleAccountsSelected = useMemo(() => {
        return totalEligibleAccounts.length === incomeSplit.accounts.length;
    }, [totalEligibleAccounts.length, incomeSplit.accounts.length]);

    const numberOfAccountsWatched =
        allEligibleAccountsSelected && verifyNumberOfAccountsWatched
            ? 'all accounts'
            : `${incomeSplit.accounts.length} account${incomeSplit.accounts.length === 1 ? '' : 's'}`;

    const incomeTypeLabel = incomeSplit.onlyExecuteOnPaychecks
        ? 'paychecks only'
        : 'all income';

    return (
        <YStack
            backgroundColor="$gray200"
            borderRadius={24}
            padding={16}
            gap={12}
        >
            <XStack alignItems="center" justifyContent="space-between">
                <XStack gap={8} alignItems="center" flex={1}>
                    <View
                        width={32}
                        height={32}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon icon={DownloadIcon} size={24} color="$gray1200" />
                    </View>
                    <BodyMediumRegular>Income</BodyMediumRegular>
                </XStack>
                <XStack gap={8} alignItems="center">
                    {statusBadge}
                    {icon != null && (
                        <View
                            onPress={onIconPress}
                            cursor="pointer"
                            width={40}
                            height={40}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon icon={icon} size={16} color="$gray1200" />
                        </View>
                    )}
                </XStack>
            </XStack>
            <YStack
                backgroundColor="$white"
                borderRadius={16}
                padding={16}
                gap={12}
            >
                <XStack gap={8} alignItems="center">
                    <BodyMediumRegular>Apply rules to</BodyMediumRegular>
                    <BodyMediumRegular color="$green900">
                        {incomeTypeLabel}
                    </BodyMediumRegular>
                </XStack>
                <XStack gap={8} alignItems="center">
                    <BodyMediumRegular>Detect income in</BodyMediumRegular>
                    <BodyMediumRegular color="$green900">
                        {numberOfAccountsWatched}
                    </BodyMediumRegular>
                </XStack>
            </YStack>
        </YStack>
    );
};
